import React from 'react';
import './TrackerStats.css';

interface TrackerStatsData {
  // Torrents
  totalTorrents: number;
  totalTorrentsSize: string;
  
  // Category breakdown
  movies: number;
  tv: number;
  music: number;
  anime: number;
  games: number;
  apps: number;
  sport: number;
  audiobook: number;
  ebook: number;
  hobby: number;
  musicProduction: number;
  other: number;
  fanres?: number;
  trailer?: number;
  liveConcert?: number;
  
  // Resolution breakdown
  res4320p: number;
  res2160p: number;
  res1080p: number;
  res1080i: number;
  res720p: number;
  res576p: number;
  res576i: number;
  res480p: number;
  res480i: number;
  resUpscale: number;
  resOther: number;
  
  // Users
  totalUsers: number;
  disabledUsers: number;
  prunedUsers: number;
  bannedUsers: number;
  activeToday: number;
  activeThisWeek: number;
  activeThisMonth: number;
  activeAll: number;
  
  // Peers
  totalSeeders: number;
  totalLeechers: number;
  totalPeers: number;
  seedSize: string;
  
  // Traffic - Real
  realUpload: string;
  realDownload: string;
  realTotal: string;
  
  // Traffic - Credited
  creditedUpload: string;
  creditedDownload: string;
  creditedTotal: string;
  
  lastUpdated: Date;
}

interface TrackerStatsProps {
  trackerName: string;
  stats: TrackerStatsData | null;
  isLoading: boolean;
  error: string | null;
  highlightedStat?: 'seedSize' | 'users' | 'torrents' | 'peers';
  isCached?: boolean;
}

const TrackerStats: React.FC<TrackerStatsProps> = ({ 
  trackerName, 
  stats, 
  isLoading, 
  error,
  highlightedStat = 'seedSize',
  isCached = false
}) => {
  if (isLoading) {
    return (
      <div className="tracker-stats">
        <div className="stats-header">
          <h2>{trackerName} Tracker Stats</h2>
        </div>
        <div className="stats-loading">
          <div className="loading-spinner"></div>
          <p>Loading tracker statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tracker-stats">
        <div className="stats-header">
          <h2>{trackerName} Tracker Stats</h2>
        </div>
        <div className="stats-error">
          <p>❌ {error}</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="tracker-stats">
        <div className="stats-header">
          <h2>{trackerName} Tracker Stats</h2>
        </div>
        <div className="stats-error">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tracker-stats">
      <div className="stats-header">
        <div className="header-title-row">
          <h2>{trackerName} Tracker Stats{isCached && <span className="cached-tag"> (cached)</span>}</h2>
          <div className="status-indicator">
            <div className="status-dot online"></div>
            <span className="status-text">Online</span>
          </div>
        </div>
        <p className="last-updated">Last updated: {stats.lastUpdated.toLocaleString()}</p>
      </div>
      
      <div className="stats-container">
        {/* Main Stats Row */}
        <div className="main-stats-row">
          <div className={`stat-card main-stat ${highlightedStat === 'torrents' ? 'highlight' : ''}`}>
            <div className="stat-icon">📁</div>
            <div className="stat-content">
              <div className="stat-label">Total Torrents</div>
              <div className="stat-value">{stats.totalTorrents.toLocaleString()}</div>
              <div className="stat-subtitle">{stats.totalTorrentsSize}</div>
            </div>
          </div>
          
          <div className={`stat-card main-stat ${highlightedStat === 'seedSize' ? 'highlight' : ''}`}>
            <div className="stat-icon">💾</div>
            <div className="stat-content">
              <div className="stat-label">Seed Size</div>
              <div className="stat-value">{stats.seedSize}</div>
              <div className="stat-subtitle">Total seeded data</div>
            </div>
          </div>
          
          <div className={`stat-card main-stat ${highlightedStat === 'users' ? 'highlight' : ''}`}>
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <div className="stat-label">Total Users</div>
              <div className="stat-value">{stats.totalUsers.toLocaleString()}</div>
              <div className="stat-subtitle">{stats.activeAll?.toLocaleString() || '0'} active</div>
            </div>
          </div>
          
          <div className={`stat-card main-stat ${highlightedStat === 'peers' ? 'highlight' : ''}`}>
            <div className="stat-icon">🔗</div>
            <div className="stat-content">
              <div className="stat-label">Total Peers</div>
              <div className="stat-value">{stats.totalPeers?.toLocaleString() || '0'}</div>
              <div className="stat-subtitle">{stats.totalSeeders?.toLocaleString() || '0'} seeders</div>
            </div>
          </div>
        </div>

        {/* Detailed Stats Grid */}
        <div className="detailed-stats-grid">

          {/* Category Stats */}
          <div className="stat-section">
            <h3>Category Breakdown</h3>
            <div className="mini-stats">
              <div className="mini-stat"><span className="label">Movies:</span> <span className="value">{stats.movies?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">TV Shows:</span> <span className="value">{stats.tv?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Music:</span> <span className="value">{stats.music?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Anime:</span> <span className="value">{stats.anime?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Games:</span> <span className="value">{stats.games?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Apps:</span> <span className="value">{stats.apps?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Sports:</span> <span className="value">{stats.sport?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Audiobooks:</span> <span className="value">{stats.audiobook?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">E-Books:</span> <span className="value">{stats.ebook?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Hobby:</span> <span className="value">{stats.hobby?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Music Production:</span> <span className="value">{stats.musicProduction?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Other:</span> <span className="value">{stats.other?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">FANRES:</span> <span className="value">{stats.fanres?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Trailer:</span> <span className="value">{stats.trailer?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Live Concert:</span> <span className="value">{stats.liveConcert?.toLocaleString() || '0'}</span></div>
            </div>
          </div>

          {/* Resolution Stats */}
          <div className="stat-section">
            <h3>Resolution Breakdown</h3>
            <div className="mini-stats">
              <div className="mini-stat"><span className="label">4320p:</span> <span className="value">{stats.res4320p?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">2160p (4K):</span> <span className="value">{stats.res2160p?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">1080p:</span> <span className="value">{stats.res1080p?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">1080i:</span> <span className="value">{stats.res1080i?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">720p:</span> <span className="value">{stats.res720p?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">576p:</span> <span className="value">{stats.res576p?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">576i:</span> <span className="value">{stats.res576i?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">480p:</span> <span className="value">{stats.res480p?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">480i:</span> <span className="value">{stats.res480i?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Upscale:</span> <span className="value">{stats.resUpscale?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Other:</span> <span className="value">{stats.resOther?.toLocaleString() || '0'}</span></div>
            </div>
          </div>

          {/* User Stats */}
          <div className="stat-section">
            <h3>User Statistics</h3>
            <div className="mini-stats">
              <div className="mini-stat"><span className="label">Active Today:</span> <span className="value">{stats.activeToday?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">This Week:</span> <span className="value">{stats.activeThisWeek?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">This Month:</span> <span className="value">{stats.activeThisMonth?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Disabled:</span> <span className="value">{stats.disabledUsers?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Pruned:</span> <span className="value">{stats.prunedUsers?.toLocaleString() || '0'}</span></div>
              <div className="mini-stat"><span className="label">Banned:</span> <span className="value">{stats.bannedUsers?.toLocaleString() || '0'}</span></div>
            </div>
          </div>

          {/* Traffic Stats */}
          <div className="stat-section">
            <h3>Traffic Statistics</h3>
            <div className="mini-stats">
              <div className="mini-stat"><span className="label">Real Upload:</span> <span className="value">{stats.realUpload}</span></div>
              <div className="mini-stat"><span className="label">Real Download:</span> <span className="value">{stats.realDownload}</span></div>
              <div className="mini-stat"><span className="label">Real Total:</span> <span className="value">{stats.realTotal}</span></div>
              <div className="mini-stat"><span className="label">Credited Upload:</span> <span className="value">{stats.creditedUpload}</span></div>
              <div className="mini-stat"><span className="label">Credited Download:</span> <span className="value">{stats.creditedDownload}</span></div>
              <div className="mini-stat"><span className="label">Credited Total:</span> <span className="value">{stats.creditedTotal}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackerStats;




